import { React, PropTypes, useRef, useEffect, reduxConnect, css, styled } from '../lib/imports';

import { HeatStreakGraph } from '../components';
import TimsHeader from '../navigation/TimsHeader';
import useElementHeight from './hooks/useElementHeight';
import HeatStreakPanel from '../heatStreak/HeatStreakPanel';
import { setNotificationBarToClosed, setHeatStreakPanelToOpen, setElementHeight } from '../ui/actions';
import { setMenuLinkClicked } from '../routing/actions';
import { setIncompletePicksModalToOpen, setPendingNavigation } from '../picks/actions';
import { useCdnImage, getAssetSet } from './context/config';
import { fireMessageEvent } from '../messenger/utils';
import { analyticActionMap } from '../analytics/utils';

const assetSet = (filename) => getAssetSet(useCdnImage, filename);

const containerBaseStyles = ({ theme, expanded }) => css`
  background-color: ${theme.colors.headerBg};
  box-shadow: 0 1px 7px 0 ${theme.colors.shadow};
  z-index: ${theme.zIndex.header};
  width: 100%;
  ${expanded ? 'position: fixed; top: 0; bottom: 0; left: 0; right: 0; overflow: scroll;' : 'position: sticky; top: 0;'}
`;

const Container = styled('header')(containerBaseStyles);

const streakContainerBaseStyles = () => css`
  cursor: pointer;
`;

const StreakContainer = styled('div')(streakContainerBaseStyles);

const prefixComponent = ({ level, percentage, onSelect }) => (
  <StreakContainer onClick={onSelect}>
    <HeatStreakGraph
      size='bit'
      gradientId={`h${level}Chart`}
      graphVariant={`h${level}`}
      variant='hot'
      percentage={percentage}
      text={level}
      showIcon
    />
  </StreakContainer>
);

const TimsHeaderContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
`;

const Header = ({
  showClose,
  showLogo,
  showPrefix,
  disableHeatstreakClick,
  showHeatStreakPanel,
  heatStreakVariant,
  heatStreakPercentage,
  showHeader,
  logoUrl,
  menuLinks,
  showNotificationBar,
  notificationBarTitle,
  notificationBarText,
  handleNotificationBarCloseClick,
  handleHeatStreakPanelClick,
  handleHeatStreakPanelClickWithModal,
  handleLinkClick,
  handleElementHeight,
  env,
  platform,
  picks,
}) => {
  const headerRef = useRef(0);
  const componentHeight = useElementHeight(headerRef);
  const { clickedType, path, buttonType, heatStreakPanelSelected } = analyticActionMap;

  useEffect(() => {
    if (!componentHeight) return;

    handleElementHeight({
      header: componentHeight,
    });
  }, [componentHeight]);

  useEffect(() => {
    if (showHeatStreakPanel) {
      const layoutEl = document.getElementById('thLayout');
      if (layoutEl) layoutEl.style.overflowY = 'hidden';
      return () => {
        if (layoutEl) layoutEl.style.overflowY = 'scroll';
      };
    }
  }, [showHeatStreakPanel]);

  const handleHeatStreakIconClick = () => {
    const { picked, picksSubmitted, picksLocked } = picks;
    const slotsPicked = Object.keys(picked)
      .map((id) => picked[id])
      .filter((id) => id);
    const hasUnsubmittedPicks = slotsPicked.length > 0 && !picksSubmitted && !picksLocked;

    if (hasUnsubmittedPicks) {
      handleHeatStreakPanelClickWithModal();
      return;
    }

    if (!showHeatStreakPanel) {
      fireMessageEvent({
        platform,
        env,
        title: clickedType,
        data: { path, element: buttonType, element_name: heatStreakPanelSelected },
      });
    }
    handleHeatStreakPanelClick();
  };

  return showHeader ? (
    <Container ref={headerRef} expanded={showHeatStreakPanel}>
      <TimsHeaderContainer>
        <TimsHeader
          showClose={showClose}
          showNav={!showHeatStreakPanel}
          routes={menuLinks}
          logoUrl={showLogo && assetSet([logoUrl])}
          hasNotificationAlert={showNotificationBar}
          notificationBarTitle={notificationBarTitle}
          notificationBarText={notificationBarText}
          handleLinkClick={(id) => handleLinkClick(id)}
          handleNotificationBarCloseClick={handleNotificationBarCloseClick}
          prefix={() =>
            showPrefix &&
            prefixComponent({
              level: heatStreakVariant,
              percentage: heatStreakPercentage,
              onSelect: disableHeatstreakClick ? undefined : handleHeatStreakIconClick,
            })
          }
        />
      </TimsHeaderContainer>
      {showHeatStreakPanel && <HeatStreakPanel />}
    </Container>
  ) : null;
};

Header.propTypes = {
  showClose: PropTypes.bool,
  showHeader: PropTypes.bool,
  menuLinks: PropTypes.array,
  showLogo: PropTypes.bool,
  showPrefix: PropTypes.bool,
  showHeatStreakPanel: PropTypes.bool,
  logoUrl: PropTypes.string,
  showNotificationBar: PropTypes.bool,
  heatStreakVariant: PropTypes.string,
  heatStreakPercentage: PropTypes.number,
  notificationBarTitle: PropTypes.string,
  notificationBarText: PropTypes.string,
  handleHeatStreakPanelClick: PropTypes.func,
  handleHeatStreakPanelClickWithModal: PropTypes.func,
  handleLinkClick: PropTypes.func,
  handleElementHeight: PropTypes.func,
  handleNotificationBarCloseClick: PropTypes.func,
  disableHeatstreakClick: PropTypes.bool,
  picks: PropTypes.object,
};

const mapStateToProps = (state) => {
  const { config, strings, routing, onboarding, ui, heatStreak, picks } = state;
  const { routeBase, language, userId, env, platform } = config;
  const { allRouteIds, routeById, currentRoute } = routing;
  const { attempts, correctAnswer } = onboarding;
  const { heatStreakPanelOpen, hasFetchError, fetchErrorCount } = ui;
  const { streak, percentage: heatStreakPercentage } = heatStreak;

  const { showNavLinks, showClose, showLogo, showPrefix } = routeById[currentRoute].displaySettings;

  const logoUrl = language === 'fr' ? 'hockeyChallengeLogoFr' : 'hockeyChallengeLogo';

  if (!showNavLinks) {
    return {
      showHeader: showClose,
      logoUrl,
      showClose,
      showLogo,
      showPrefix,
      menuLinks: [],
      showNotificationBar: attempts > 0 && !correctAnswer,
      notificationBarTitle: strings['notifications/quiz/wrongAnswer/title'],
      notificationBarText: strings['notifications/quiz/wrongAnswer/text'],
    };
  }

  const heatStreakVariant = streak.toString();

  const menuLinks = allRouteIds
    .map((id) => {
      const route = routeById[id];

      return {
        ...route,
        path: `${routeBase}${route.path}`,
        search: `?timsLang=${language}&userId=${userId}&env=${env}`,
        isDisabled: hasFetchError && fetchErrorCount < 3,
        label: strings[`routes/${route.id}/label`] || route.label,
      };
    })
    .filter(({ hideInNav }) => !hideInNav);

  const notificationBarSettings = {
    showNotificationBar: false,
    notificationBarTitle: '',
    notificationBarText: '',
  };

  return {
    env,
    platform,
    strings,
    logoUrl,
    showClose,
    showLogo,
    showPrefix,
    disableHeatstreakClick: hasFetchError,
    showHeader: showClose || !!menuLinks.length,
    showHeatStreakPanel: heatStreakPanelOpen,
    heatStreakVariant,
    heatStreakPercentage,
    menuLinks,
    picks,
    ...notificationBarSettings,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    handleElementHeight: (height) => dispatch(setElementHeight(height)),
    handleNotificationBarCloseClick: () => dispatch(setNotificationBarToClosed()),
    handleHeatStreakPanelClick: () => dispatch(setHeatStreakPanelToOpen()),
    handleHeatStreakPanelClickWithModal: () => {
      dispatch(setPendingNavigation('heatstreak'));
      dispatch(setIncompletePicksModalToOpen());
    },
    handleLinkClick: (id) => dispatch(setMenuLinkClicked(id)),
  };
};

export default reduxConnect(mapStateToProps, mapDispatchToProps)(Header);
